/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * This lint check is disabled because we must ommit the `quotation` reference from the useEffect dependency
 * array, otherwise selecting a new unit will update the `selectedPackage` property and cause a new quotation
 * to be generated, thus calling the effect again and rendering the first page, forcing an infinote loop.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parse } from 'query-string';
import { connect, ConnectedProps } from 'react-redux';
import { format } from 'date-fns';
import { useTheme, useMediaQuery } from '@material-ui/core';

// Data Layer
import {
  Quotation,
  EmissionLog,
  ProductConfiguration,
  CarPolicy,
  RequestIndicatorTypes,
} from 'data-layer-cr/dist/models';
import {
  PENDING_INSPECTION,
  PENDING_EMISSION,
  PROCESSING_EMISSION,
} from 'data-layer-cr/dist/constants/quotationStatus';
import { defaultUnitEntity } from 'data-layer-cr/dist/functions/quotations';
import { isActive } from 'data-layer-cr/dist/functions/carPolicies';
import hasUpcomingCancellation from 'src/functions/hasUpcomingCancellation';
import { isInspectionRequired } from 'data-layer-cr/dist/functions/quotations/quotationMethods';
import {
  isLoggedIn,
  getLoggedRole,
  getLoggedEmail,
  getLoggedLicense,
  getLoggedUserId,
  getLoggedUserName,
} from 'data-layer-cr/dist/authentication';
import { paymentCodeForStatusId } from 'data-layer-cr/dist/functions/paymentCodes';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { requestExistingPoliciesForCarInfo } from 'data-layer-cr/dist/slices/carPolicies/actions';

// Analytics
import {
  login,
  setUserProperties,
  trackEvent,
  QUOTATION_ID,
  CURRENT_STEP,
  START_DATE,
  WELCOME_EMISSION_PAGE_STEP,
  EMISSION_BY,
  ORIGIN,
} from 'src/analytics';

// Redux
import { REQUEST_COVERAGE_CATALOG } from 'src/store/coverageCatalog/requestIdentifiers';
import { REQUEST_QUOTATION } from 'src/store/quotation/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';
import {
  getQuotation,
  setLocalPackageOption,
} from 'src/store/quotation/actions';
import { getEmission } from 'src/store/emissionPolicy/actions';
import { REQUEST_CONSULT_EMISSION_DATA } from 'src/store/emissionPolicy/requestIdentifiers';
import { requestCoverageCatalogs } from 'src/store/coverageCatalog/actions';
import {
  setStartedEmission,
  setOnPackageSelection,
  // setForcePagePayment,
} from 'src/store/steppers/actions';
import { updateUserData } from 'src/store/profile/actions';

// Functions
import {
  attemptToExtractEmissionStatusCode,
  isProcessingEmission,
  shouldShowSuccessScreen,
  isOnError,
} from 'src/functions/quotationSemaphoreMethods';
import validateSelectedPackage from 'src/functions/validateSelectedPackage';

// Components
import EmissionPolicyManager from '../EmissionPolicy';
import InspectionFormManager from '../Inspection';
import EmissionWelcome from '../EmissionWelcome';
import InspectionWelcome from '../InspectionWelcome';
import UnitSelection from '../UnitSelection';
import StandaloneFinalEmissionScreen from '../StandaloneFinalEmissionScreen';
import InspectionRedirection from '../InspectionRedirection';
import { ErrorSubScreen, LoadingSubScreen } from './components';

import { setStartedInspection } from '../../../store/steppers/actions';
import { setExclusionDate } from '../../../store/emissionPolicy/actions';
import { setQuotationStatus } from '../../../store/quotation/actions';

import NetworkClient from '../../../services/NetworkClient';

/**
 * Checks the provided data and, based on internal conditions, will force the process to an alternate
 * version of the final screen on the emission step.
 * This is done with a new screen instead of navigating to the final emission step because we must prevent
 * the user from trying to trigger a "back" navigation (and the payment error should not automatically trigger
 * it either), as we don't actually have the form data and the request cannot be retried.
 *
 * Returns true if the skip is applied.
 *
 * @param quotation - The quotation provide by the service
 * @param emissionLog - The emission log provided by the service
 * @param isStartedEmision - Whether the emission was already previously started (persist related)
 * @param showStandaloneFinalScreen - The method that triggers the screen change.
 */
function attemptSkipToFinalEmissionStep(
  quotation: Quotation,
  emissionLog: EmissionLog | null,
  isStartedEmision: boolean,
  showStandaloneFinalScreen: () => void,
): boolean {
  const statusCode = paymentCodeForStatusId(
    attemptToExtractEmissionStatusCode(emissionLog),
  );
  const mustSkip =
    (isProcessingEmission(quotation, statusCode) && !isStartedEmision) ||
    shouldShowSuccessScreen(quotation, statusCode) ||
    isOnError(quotation, statusCode);
  if (mustSkip) {
    showStandaloneFinalScreen();
  }
  return mustSkip;
}

/**
 * Shows the appropiate loading layout based on the provided requestingData parameter.
 * Note that this function was extracted to help reduce the cognitive complexity reported by
 * sonarcloud on the dashboardScreen component.
 *
 * Will return true if the skip is applied.
 *
 * @param requestingData - Whether the data is being requested
 * @param showLoading - The method that actually displays the loading layout
 */
function attemptShowLoading(
  requestingData: boolean,
  showLoading: () => void,
): boolean {
  if (requestingData) {
    showLoading();
  }
  return requestingData;
}

/**
 * Checks if the emission welcome step must be skipped based on the provided parameters.
 * Will return true if the skip is applied.
 */
type AttemptSkipEmissionWelcomeProps = {
  /** Whether we need to display the unit selection screen, or go directly to the emission form */
  mustGoToUnitSelection: boolean;
  /** Method used to display the emission unit selection screen */
  showEmissionUnitSelection: () => void;
  /** Method used to display the actual emission screen */
  showEmission: () => void;

  updateUserDataAction: (
    role: string,
    email: string,
    license: string,
    userName: string,
    userId: string,
  ) => void;
};
function attemptSkipEmissionWelcome({
  mustGoToUnitSelection,
  showEmissionUnitSelection,
  showEmission,
  updateUserDataAction,
}: AttemptSkipEmissionWelcomeProps): boolean {
  const role = getLoggedRole();
  const email = getLoggedEmail();
  const license = getLoggedLicense();
  const userName = getLoggedUserName();
  const userId = getLoggedUserId();
  const mustSkip = isLoggedIn();
  if (mustSkip) {
    updateUserDataAction(role, email, license, userName, userId);
    if (mustGoToUnitSelection) {
      showEmissionUnitSelection();
    } else {
      showEmission();
    }
  }
  return mustSkip;
}

/**
 * Will return true if we must enter the inspection workflow, based on the provide parameters.
 *
 * @param quotation - The quotation returned by the service
 * @param inspectionRequired - Whether the inspection workflow is required based on initial configurations
 * @param quotationStatusId - The quotation's current status
 */
function mustEnterInspectionWorkflow(
  quotation: Quotation,
  inspectionRequired: boolean,
  quotationStatusId: number,
): boolean {
  return (
    isInspectionRequired(quotation, inspectionRequired) &&
    quotationStatusId === PENDING_INSPECTION
  );
}

/**
 * Will try to display the inspection workflow, based on the provide parametrers
 */
type AttemptInspectionWorkflowProps = {
  /** The quotation returned by the service */
  quotation: Quotation;
  /** Whether the configuration for this quotation's product requires inspection at all */
  inspectionRequired: boolean;
  /** The status for the provide quotation */
  quotationStatusId: number;
  /** Whether the inspection process was already started on a previous attempt (persist related) */
  isStartedInspection: boolean;
  /** Method used to show the actual inspection process */
  showInspection: () => void;
  /** Method used to show the inspection welcome screen */
  showInspectionWelcome: () => void;
  /** Whether is usign the app from desktop or mobile */
  isMobile: boolean;
  /** Method used to show the desktop page for inspection */
  showDesktopInspection: () => void;
};
function attemptInspectionWorkflow({
  quotation,
  inspectionRequired,
  quotationStatusId,
  isStartedInspection,
  isMobile,
  showInspection,
  showInspectionWelcome,
  showDesktopInspection,
}: AttemptInspectionWorkflowProps): boolean {
  const appliesWorkflow = mustEnterInspectionWorkflow(
    quotation,
    inspectionRequired,
    quotationStatusId,
  );
  if (appliesWorkflow) {
    if (!isMobile) {
      showDesktopInspection();
    } else if (isStartedInspection) {
      showInspection();
    } else {
      showInspectionWelcome();
    }
  }
  return appliesWorkflow;
}

export const validateQuotationOrigin = (
  quotationOrigin: string | null,
): string => (quotationOrigin !== null ? quotationOrigin : '');

export const trackEmissionWelcome = (quotation: Quotation): void => {
  login(quotation.id.toString());
  setUserProperties({
    $distinct_id: quotation.id.toString(),
    $name: `${quotation.customer.firstName} ${quotation.customer.lastName}`,
    $email: quotation.customer.email,
    [CURRENT_STEP]: WELCOME_EMISSION_PAGE_STEP,
    [QUOTATION_ID]: quotation.id.toString(),
    [EMISSION_BY]: quotation.user.role,
    [ORIGIN]: validateQuotationOrigin(quotation.summary.origin),
  });
  trackEvent({
    eventName: WELCOME_EMISSION_PAGE_STEP,
    eventProperties: {
      [START_DATE]: new Date().toLocaleString(),
    },
  });
};

export const DashboardScreen = ({
  quotation,
  emissionLog,
  productConfiguration,
  isStartedInspection,
  isStartedEmission,
  isOnPackageSelection,
  requestGetEmission,
  requestGetQuotation,
  requestCoverageCatalogsAction,
  setStartedEmissionAction,
  setOnPackageSelectionAction,
  updateUserDataAction,
  emissionStatusCode,
  setStartedInspectionAction,
  setExclusionDateAction,
  requestExistingPoliciesForCarInfoAction,
  carPolicy,
  validateCarInfoState,
  setQuotationStatusAction,
  forcePaymentPage,
  emissionOrderId,
  setPackageOptionAction,
}: PropsFromRedux): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const values = parse(location.search);
  const [content, updateContent] = useState(<div />);
  const hash: string = values.id?.toString() || '';
  const calculatorPackage = validateSelectedPackage(values.package?.toString());
  const { productId, user, packageOption } = quotation;

  const requestQuotationState = useNetworkObserver(REQUEST_QUOTATION);
  const requestingProductConfigurationStatus = useNetworkObserver(
    REQUEST_COVERAGE_CATALOG,
  );
  const requestEmissionState = useNetworkObserver(
    REQUEST_CONSULT_EMISSION_DATA,
  );

  const quotationStatusId = useMemo(
    () => quotation.status.id,
    [quotation.status.id],
  );

  /** Saves the current url to differentiate between different quotation attempts */
  useEffect(() => {
    localStorage.setItem('url', window.location.href);
  }, []);

  /** Select package when coming from calculator */
  useEffect(() => {
    if (calculatorPackage) {
      setPackageOptionAction(calculatorPackage);
      setStartedEmissionAction(true);
      setOnPackageSelectionAction(false);
    }
  }, [calculatorPackage]);

  /**
   * Try to force payment page
   */
  useEffect(() => {
    if (forcePaymentPage && Boolean(emissionOrderId)) {
      navigate(`/emission/continue?accepted=True&uniqueId=${emissionOrderId}`);
    }
  }, [forcePaymentPage, emissionOrderId]);

  /**
   * Try to load the quotation for the provided hash
   */
  useEffect(() => {
    if (emissionStatusCode === '') {
      requestGetQuotation(hash);
    }
  }, [requestGetQuotation, hash]);

  useEffect(() => {
    if (
      requestQuotationState === 'success' &&
      [PENDING_EMISSION, PROCESSING_EMISSION].includes(quotationStatusId)
    ) {
      requestGetEmission(hash);
    }
  }, [requestQuotationState, quotationStatusId, hash]);

  useEffect(() => {
    const unit = defaultUnitEntity(quotation);

    if (
      quotation.id > 0 &&
      !unit.isNewCar &&
      validateCarInfoState !== 'success'
    ) {
      requestExistingPoliciesForCarInfoAction(
        unit.licensePlate,
        unit.serialChassis,
        unit.serialMotor,
        NetworkClient,
      );
    }
  }, [quotation]);

  const isActivePolicy = useMemo(() => isActive(carPolicy), [carPolicy]);

  const hasUpcomingCancell = useMemo(
    () => hasUpcomingCancellation(carPolicy),
    [carPolicy],
  );

  const exclusionDateFromPolicy = useMemo(
    () => carPolicy?.effectiveEndDate,
    [carPolicy],
  );

  /**
   * Try to load the product configuration for the quotation. Only perform this
   * request AFTER the configuration loads successfully.
   */
  useEffect(() => {
    if (productId !== '' && emissionStatusCode === '') {
      requestCoverageCatalogsAction(
        productId,
        user.id,
        quotation.quotationProfile.id,
        quotation.units[0].unit.use,
      );
    }
  }, [requestCoverageCatalogsAction, productId]);

  /**
   * Will return true if not all the required data has finished loading
   */
  const requestingData = useMemo(
    () =>
      requestQuotationState === 'requesting' ||
      (requestQuotationState !== 'failure' &&
        ['initial', 'requesting'].includes(
          requestingProductConfigurationStatus,
        )) ||
      validateCarInfoState === 'requesting' ||
      requestEmissionState === 'requesting',
    [
      requestQuotationState,
      requestEmissionState,
      requestingProductConfigurationStatus,
      validateCarInfoState,
    ],
  );

  /**
   * Will return true if both the quotation and the configuration requests finish
   * successfully.
   */
  const dataRequestedSuccessfully = useMemo(
    () =>
      requestQuotationState === 'success' &&
      requestingProductConfigurationStatus === 'success' &&
      ['initial', 'success'].includes(validateCarInfoState) &&
      ['initial', 'success'].includes(requestEmissionState),
    [
      requestQuotationState,
      requestEmissionState,
      requestingProductConfigurationStatus,
      validateCarInfoState,
    ],
  );

  /**
   * Displays a loading layout.
   */
  function showLoading(): void {
    updateContent(<LoadingSubScreen data-testid="loading-screen" />);
  }

  /** Displays an error layout */
  function showError(): void {
    updateContent(<ErrorSubScreen data-testid="error-screen" />);
  }

  /** Displays a component that encapsulates the final step in the emission process */
  const showStandaloneFinalScreen = useCallback(
    () =>
      updateContent(
        <StandaloneFinalEmissionScreen data-testid="standalone-final-screen" />,
      ),
    [],
  );

  /**
   * This specific dependency needs to have only "id" in the dependency array, otherwise changing the
   * packageOption inside quotation will force it to recalculate and the navigation will be foreced to
   * the EmissionWelcome screen
   */
  const mustGoToUnitSelection = useMemo(
    () => packageOption === 0,
    [packageOption],
  );

  /**
   * Displays the emission's welcome screen.
   */
  const showEmissionWelcome = useCallback(() => {
    updateContent(
      <EmissionWelcome
        data-testid="emission-welcome"
        onStartRequested={(): void => {
          trackEmissionWelcome(quotation);
          if (mustGoToUnitSelection) {
            showEmissionUnitSelection();
          } else {
            showEmission();
          }
        }}
      />,
    );
  }, [mustGoToUnitSelection]);

  /**
   * Displays the package selection screen, before the emission form.
   */
  const showEmissionUnitSelection = useCallback(() => {
    setOnPackageSelectionAction(true);
    if (isActivePolicy) {
      navigate('/validationpolicy');
    } else {
      if (hasUpcomingCancell) {
        const exlusionDate = new Date(exclusionDateFromPolicy as string);
        exlusionDate.setDate(exlusionDate.getDate() + 1);
        exlusionDate.setHours(0, 0, 0, 0);
        setExclusionDateAction(format(exlusionDate, 'dd/MM/yyyy'));
      }
      updateContent(
        <UnitSelection
          data-testid="unit-selection-screen"
          onNextRequested={showEmission}
          onGoToEmissionWelcome={showEmissionWelcome}
        />,
      );
    }
  }, [showEmissionWelcome, hasUpcomingCancell, isActivePolicy]);

  /**
   * Displays the emission form.
   * The "no-use-before-defined" rule had to be disabled here, due to how this screen and the unit
   * selection screen are related to each other. It was either here, or on the selection screen
   * definition.
   */
  const showEmission = useCallback(() => {
    setOnPackageSelectionAction(false);
    setStartedEmissionAction(true);
    updateContent(
      <EmissionPolicyManager
        data-testid="emission-form"
        onGoToUnitSelection={showEmissionUnitSelection}
      />,
    );
  }, [showEmissionUnitSelection]);

  /**
   * Displays Inspection redirection
   */
  const showDesktopInspection = useCallback(() => {
    updateContent(
      <InspectionRedirection data-testid="inspection-redirection" />,
    );
  }, []);

  /**
   * Displays the inspection form
   */
  const showInspection = useCallback(() => {
    setStartedInspectionAction(true);
    updateContent(
      <InspectionFormManager
        data-testid="inspection-form"
        onGoToEmissionRequested={(): void => {
          setQuotationStatusAction(PENDING_EMISSION);
        }}
        onGoToWelcomeInspection={showInspectionWelcome}
      />,
    );
  }, []);

  /**
   * Displays the inspection's welcome screen.
   */
  const showInspectionWelcome = useCallback(() => {
    updateContent(
      <InspectionWelcome
        data-testid="inspection-welcome"
        onStartRequested={showInspection}
      />,
    );
  }, [showInspection]);

  useEffect(() => {
    const { inspectionRequired } = productConfiguration;

    /**
     * Determine which screen needs to be rendered. Note that
     * each render should also stop any additional execution to avoid falling
     * into the "fallback" error logic.
     */
    if (attemptShowLoading(requestingData, showLoading)) {
      return;
    }

    /**
     * Data requested successfully, try to enter either the emission or the inspection workflow
     */
    if (dataRequestedSuccessfully) {
      /**
       * The first check after a successfull network request is to check if the emission process already
       * started, which will skip to a modified version of the final page
       */
      if (
        attemptSkipToFinalEmissionStep(
          quotation,
          emissionLog,
          isStartedEmission,
          showStandaloneFinalScreen,
        )
      ) {
        return;
      }

      /** Skip subsequent logic if entering the inspection workflow */
      if (
        attemptInspectionWorkflow({
          quotation,
          inspectionRequired,
          quotationStatusId,
          showInspection,
          showInspectionWelcome,
          isStartedInspection,
          isMobile,
          showDesktopInspection,
        })
      ) {
        return;
      }

      /** Logic for the emission workflow, including skipping the welcome page if a session is detected */
      if (isOnPackageSelection) {
        showEmissionUnitSelection();
        return;
      }
      if (isStartedEmission) {
        showEmission();
        return;
      }
      if (
        attemptSkipEmissionWelcome({
          mustGoToUnitSelection,
          showEmissionUnitSelection,
          showEmission,
          updateUserDataAction,
        })
      ) {
        return;
      }
      showEmissionWelcome();
      return;
    }

    /**
     * Render an error if no other screen is rendered, and at least one of the
     * request failed.
     */
    showError();
  }, [
    quotationStatusId,
    productConfiguration,
    showEmissionWelcome,
    showInspectionWelcome,
    requestingData,
    dataRequestedSuccessfully,
    updateUserData,
    showStandaloneFinalScreen,
  ]);

  return content;
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  emissionLog: EmissionLog | null;
  productConfiguration: ProductConfiguration;
  isStartedInspection: boolean;
  isStartedEmission: boolean;
  isOnPackageSelection: boolean;
  emissionStatusCode: string;
  carPolicy?: CarPolicy;
  validateCarInfoState: RequestIndicatorTypes;
  forcePaymentPage: boolean;
  emissionOrderId: string;
} => ({
  quotation: state.quotation.quotation,
  emissionLog: state.quotation.emissionLog,
  productConfiguration: state.coverageCatalog.productConfig,
  isStartedInspection: state.stepper.isStartedInspection,
  isStartedEmission: state.stepper.isStartedEmision,
  isOnPackageSelection: state.stepper.isOnPackageSelection,
  emissionStatusCode: state.emissionPolicy.emissionStatusCode,
  carPolicy: state.carPolicies.mostRecentCarPolicy,
  validateCarInfoState: state.carPolicies.validateAllCarInfoRequestStatus,
  forcePaymentPage: state.stepper.forcePaymentPage,
  emissionOrderId: state.emissionPolicy.emissionOrderId,
});

const reduxActions = {
  requestGetQuotation: getQuotation,
  setPackageOptionAction: setLocalPackageOption,
  requestCoverageCatalogsAction: requestCoverageCatalogs,
  setStartedEmissionAction: setStartedEmission,
  setOnPackageSelectionAction: setOnPackageSelection,
  updateUserDataAction: updateUserData,
  setStartedInspectionAction: setStartedInspection,
  requestExistingPoliciesForCarInfoAction: requestExistingPoliciesForCarInfo,
  setExclusionDateAction: setExclusionDate,
  requestGetEmission: getEmission,
  setQuotationStatusAction: setQuotationStatus,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardScreen);
